import React, { useState, useRef, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import ShoppableAdPreview, { ProductChip, PRODUCTS } from './ShoppableAdPreview';
import webDemoVideo from '../../../../assets/creative/shoppable/Video_16_9.mp4';
import webDemoDisplay from '../../../../assets/creative/shoppable/display_16_9.jpg';
import webDemoBackground from '../../../../assets/creative/shoppable/web_preview_background2.png';
import '../innovation-ads.css';

const LAYOUT_POSITIONS = {
    horizontalPanel: ['Footer'],
    overlay: ['Footer', 'Left', 'Right'],
    sidePanel: ['Right', 'Left'],
};

const formatFileSize = (bytes) => `${(bytes / (1024 * 1024)).toFixed(1)} MB`;

const ShoppableConfigure = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const fileInputRef = useRef(null);

    // Which creative to show: 'olv' (video) or 'display' (static banner) —
    // set by which button was clicked on the Innovation Ads listing page.
    const format = location.state?.format === 'display' ? 'display' : 'olv';

    const [feedSource, setFeedSource] = useState('uploadCsv'); // 'uploadCsv' | 'feedUrl'
    const [feedFile, setFeedFile] = useState(null);
    const [feedUrl, setFeedUrl] = useState('');
    const [panelStyle] = useState('carousel'); // 'carousel' | 'grid'
    const [ctaText, setCtaText] = useState('Book Now');
    const [autoSync, setAutoSync] = useState(true);
    const [showWebDemo, setShowWebDemo] = useState(false);
    // Same delayed reveal as everywhere else — the overlay starts hidden and
    // fades in after 2s, using the same .ia-overlay-decoration opacity
    // transition, instead of just appearing instantly.
    const [webDemoAdVisible, setWebDemoAdVisible] = useState(false);

    useEffect(() => {
        setWebDemoAdVisible(false);
        if (!showWebDemo) return undefined;
        const timer = setTimeout(() => setWebDemoAdVisible(true), 2000);
        return () => clearTimeout(timer);
    }, [showWebDemo]);
    // Layout is locked to Overlay > Footer for now — the Product Panel Style,
    // LAYOUT, and Position controls are hidden from the form.
    const layout = 'overlay';
    const position = LAYOUT_POSITIONS.overlay[0];

    const handleFileChange = (e) => {
        const file = e.target.files[0];
        if (file) setFeedFile(file);
    };

    return (
        <div className="ia-page">
            <button type="button" className="ia-back-link" onClick={() => navigate('/innovation-ads')}>
                <i className="fa-solid fa-arrow-left"></i> Back to Innovation Ads
            </button>

            <h2 className="ia-title">Shoppable Ads</h2>
            <p className="ia-subheading">Configure your shoppable ads enhancement settings.</p>

            <div className="ia-configure-grid">
                <div className="ia-panel">
                    <h3 className="ia-panel-title">Configure</h3>

                    <label className="ia-field-label">Product Feed Source</label>
                    <div className="ia-radio-group">
                        <label className="ia-radio-option">
                            <input
                                type="radio"
                                name="feedSource"
                                checked={feedSource === 'uploadCsv'}
                                onChange={() => setFeedSource('uploadCsv')}
                            />
                            Upload CSV
                        </label>
                        <label className="ia-radio-option">
                            <input
                                type="radio"
                                name="feedSource"
                                checked={feedSource === 'feedUrl'}
                                onChange={() => setFeedSource('feedUrl')}
                            />
                            Product Feed URL
                        </label>
                    </div>

                    {feedSource === 'uploadCsv' ? (
                        <>
                            <input
                                ref={fileInputRef}
                                type="file"
                                accept=".csv"
                                style={{ display: 'none' }}
                                onChange={handleFileChange}
                            />
                            {feedFile ? (
                                <div className="ia-file-chip">
                                    <i className="fa-solid fa-file-csv ia-file-icon"></i>
                                    <div className="ia-file-chip-info">
                                        <div className="ia-file-chip-name">{feedFile.name}</div>
                                        <div className="ia-file-chip-size">{formatFileSize(feedFile.size)}</div>
                                    </div>
                                    <i className="fa-solid fa-circle-check ia-file-check"></i>
                                    <button
                                        type="button"
                                        className="ia-file-chip-change"
                                        onClick={() => fileInputRef.current.click()}
                                    >
                                        Change File
                                    </button>
                                </div>
                            ) : (
                                <button
                                    type="button"
                                    className="ia-file-picker-btn"
                                    onClick={() => fileInputRef.current.click()}
                                >
                                    <i className="fa-solid fa-upload"></i> Choose CSV file
                                </button>
                            )}
                        </>
                    ) : (
                        <input
                            type="text"
                            className="ia-field-input"
                            placeholder="https://"
                            value={feedUrl}
                            onChange={(e) => setFeedUrl(e.target.value)}
                        />
                    )}

                    <label className="ia-field-label">Call to Action Text</label>
                    <input
                        type="text"
                        className="ia-field-input"
                        value={ctaText}
                        onChange={(e) => setCtaText(e.target.value)}
                    />

                    <div className="ia-divider" />

                    <div className="ia-toggle-row">
                        <button
                            type="button"
                            className={`ia-switch ${autoSync ? 'on' : ''}`}
                            onClick={() => setAutoSync(!autoSync)}
                            aria-pressed={autoSync}
                        >
                            <span className="ia-switch-knob" />
                        </button>
                        <span className="ia-toggle-text">Product Feed Updates</span>
                    </div>
                    <p className="ia-toggle-help">Automatically sync products from your feed.</p>

                    <div className="ia-panel-footer">
                        <button type="button" className="ia-btn ia-btn-plain" onClick={() => navigate('/innovation-ads')}>
                            Cancel
                        </button>
                        <button
                            type="button"
                            className="ia-btn ia-btn-primary"
                            onClick={() => setShowWebDemo(true)}
                        >
                            Generate Preview On Web Page
                        </button>
                    </div>
                </div>

                <div className="ia-panel">
                    <h3 className="ia-panel-title">Preview</h3>
                    <div className="ia-preview-wrap">
                        <div className="ia-preview-frame-phone ia-preview-frame-phone-fullbleed">
                            <div className="ia-preview-phone-statusbar">
                                <span className="icons">
                                    <i className="fa-solid fa-signal"></i>
                                    <i className="fa-solid fa-wifi"></i>
                                    <i className="fa-solid fa-battery-full"></i>
                                </span>
                            </div>
                            <ShoppableAdPreview
                                layout={layout}
                                position={position}
                                panelStyle={panelStyle}
                                ctaText={ctaText}
                                format={format}
                            />
                        </div>
                        <p className="ia-simulated-note">
                            <i className="fa-solid fa-circle-info"></i>
                            This is a simulated preview. Actual appearance may vary by device and inventory.
                        </p>
                    </div>
                </div>
            </div>

            {showWebDemo && (
                <div className="ia-modal-overlay" onClick={() => setShowWebDemo(false)}>
                    <div className="ia-webdemo-modal" onClick={(e) => e.stopPropagation()}>
                        <button
                            type="button"
                            className="ia-modal-close ia-webdemo-close"
                            onClick={() => setShowWebDemo(false)}
                            aria-label="Close"
                        >
                            <i className="fa-solid fa-xmark"></i>
                        </button>

                        <div
                            className="ia-webdemo-site ia-webdemo-site-bg"
                            style={{ backgroundImage: `url(${webDemoBackground})` }}
                        >
                            <div className="ia-webdemo-ad-slot">
                                {format === 'display' ? (
                                    <img className="ia-webdemo-ad-media" src={webDemoDisplay} alt="Ad creative" />
                                ) : (
                                    <video
                                        className="ia-webdemo-ad-media"
                                        src={webDemoVideo}
                                        autoPlay
                                        loop
                                        muted
                                        playsInline
                                    />
                                )}
                                <div className={`ia-webdemo-ad-overlay ia-overlay-decoration ${webDemoAdVisible ? 'visible' : ''}`}>
                                    <ProductChip product={PRODUCTS[0]} ctaText={ctaText} />
                                </div>
                            </div>
                        </div>

                        <p className="ia-simulated-note ia-webdemo-note">
                            <i className="fa-solid fa-circle-info"></i>
                            This is a simulated publisher page for demo purposes — layout and content are illustrative only.
                        </p>
                    </div>
                </div>
            )}
        </div>
    );
};

export default ShoppableConfigure;
